import { useState } from 'react'
import { updateUserSettings } from '../../api/users'
import { useAuthStore } from '../../store/auth'
import { useDefaultsStore } from '../../store/defaults'
import { useThemeStore } from '../../store/theme'
import { PALETTES, paletteToTheme } from '../../lib/palettes'
import type { Palette } from '../../lib/palettes'

interface Props {
  onComplete: () => void
}

type Preset = 'private' | 'friends-only' | 'public'

const PRESET_SETTINGS = {
  private: {
    visibility: 0,
    default_entry_privacy: 0,
    default_reply_privacy: 0,
    default_upload_privacy: 0,
    default_tagset_privacy: 0,
    follow_request_policy: 'none',
  },
  'friends-only': {
    visibility: 2,
    default_entry_privacy: 1,
    default_reply_privacy: 1,
    default_upload_privacy: 1,
    default_tagset_privacy: 1,
    follow_request_policy: 'friends',
  },
  public: {
    visibility: 3,
    default_entry_privacy: 3,
    default_reply_privacy: 3,
    default_upload_privacy: 3,
    default_tagset_privacy: 3,
    follow_request_policy: 'anyone',
  },
}

const PRESET_DESCRIPTIONS: Record<Preset, string> = {
  private: 'only you can see your journal and profile',
  'friends-only': 'followers you approve can see your content',
  public: 'anyone on the internet can read your journal',
}

const CARD_STYLE: React.CSSProperties = {
  border: '1px solid var(--color-fg)',
  padding: '1rem 1.25rem',
  marginBottom: '0.75rem',
  cursor: 'pointer',
  textAlign: 'left',
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  font: 'inherit',
}

const SELECTED_CARD_STYLE: React.CSSProperties = {
  ...CARD_STYLE,
  border: '2px solid var(--color-1)',
}

function PaletteSwatchCard({
  palette,
  selected,
  onClick,
}: {
  palette: Palette
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        border: selected ? '2px solid var(--color-1)' : '1px solid var(--color-fg)',
        padding: '0.75rem',
        cursor: 'pointer',
        background: palette.bg,
        color: palette.fg,
        font: 'inherit',
        textAlign: 'left',
        width: '100%',
        marginBottom: '0.5rem',
      }}
    >
      <span style={{ fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>{palette.name}</span>
      <span style={{ display: 'flex', gap: '0.25rem' }}>
        {[palette.bg, palette.fg, palette.color1, palette.color2].map((c, i) => (
          <span
            key={i}
            style={{ display: 'inline-block', width: 18, height: 18, background: c, border: '1px solid rgba(128,128,128,0.3)' }}
          />
        ))}
      </span>
    </button>
  )
}

export function UserOnboardingWizard({ onComplete }: Props) {
  const username = useAuthStore((s) => s.username)!
  const setDefaults = useDefaultsStore((s) => s.setDefaults)
  const { setUserTheme } = useThemeStore()

  const [step, setStep] = useState<1 | 2>(1)
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null)
  const [selectedPalette, setSelectedPalette] = useState<Palette | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleFinish(palette: Palette | null) {
    setSaving(true)
    setError('')
    try {
      if (selectedPreset) {
        const settings = PRESET_SETTINGS[selectedPreset]
        await updateUserSettings(username, settings)
        setDefaults({
          entry: settings.default_entry_privacy,
          reply: settings.default_reply_privacy,
          upload: settings.default_upload_privacy,
          tagset: settings.default_tagset_privacy,
        })
      }
      if (palette) {
        const theme = paletteToTheme(palette)
        await updateUserSettings(username, { color_theme: theme })
        setUserTheme(theme)
      }
      sessionStorage.removeItem('abaddon-onboarding-pending')
      onComplete()
    } catch {
      setError('something went wrong — you can adjust these in settings')
      setSaving(false)
    }
  }

  if (step === 1) {
    return (
      <div>
        <h2 style={{ marginBottom: '0.5rem' }}>who can see your journal?</h2>
        <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', opacity: 0.7 }}>
          you can change this any time in settings
        </p>
        {(Object.keys(PRESET_DESCRIPTIONS) as Preset[]).map((preset) => (
          <button
            key={preset}
            onClick={() => setSelectedPreset(preset)}
            style={selectedPreset === preset ? SELECTED_CARD_STYLE : CARD_STYLE}
          >
            <strong>{preset}</strong>
            <span style={{ display: 'block', fontSize: '0.85rem', marginTop: '0.25rem', opacity: 0.7 }}>
              {PRESET_DESCRIPTIONS[preset]}
            </span>
          </button>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
          <button onClick={() => setStep(2)} style={{ opacity: 0.6, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
            skip
          </button>
          <button
            onClick={() => setStep(2)}
            disabled={!selectedPreset}
            style={{ opacity: selectedPreset ? 1 : 0.3 }}
          >
            next
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginBottom: '0.5rem' }}>pick a color theme</h2>
      <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', opacity: 0.7 }}>
        you can change or customize this in settings
      </p>
      <button
        onClick={() => setSelectedPalette(null)}
        style={selectedPalette === null ? SELECTED_CARD_STYLE : CARD_STYLE}
      >
        no theme
      </button>
      {PALETTES.map((p) => (
        <PaletteSwatchCard
          key={p.id}
          palette={p}
          selected={selectedPalette?.id === p.id}
          onClick={() => setSelectedPalette(p)}
        />
      ))}
      {error && <p style={{ color: 'var(--color-1)', marginTop: '0.75rem' }}>{error}</p>}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
        <button onClick={() => setStep(1)} style={{ opacity: 0.6, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
          back
        </button>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => handleFinish(null)}
            disabled={saving}
            style={{ opacity: 0.6, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit' }}
          >
            skip
          </button>
          <button onClick={() => handleFinish(selectedPalette)} disabled={saving}>
            {saving ? 'saving…' : 'get started'}
          </button>
        </div>
      </div>
    </div>
  )
}
